import { useEffect, useMemo, useState } from "react";
import SegmentedComponent from "../../../../components/segmented/Segmented.jsx";
import Loader from "../../../../components/Loader.jsx";
import CreateLiveAccount from "../../dashboard/components/CreateLiveAccount.jsx";
import {
  GetAllMT5AccountApi,
  GetSocialTradingTokenApi,
} from "../../../../api/request/account.js";
import { useResponsive } from "../../../../hooks/useResponsive.js";

const SOCIAL_TRADING_HOST = "https://social.upforex.com";
const SOCIAL_TRADING_REGISTRATION_URL = `${SOCIAL_TRADING_HOST}/portal/login`;

const SocialTrading = () => {
  const { isMobile } = useResponsive();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [token, setToken] = useState(null);
  const [registrationUrl, setRegistrationUrl] = useState(null);
  const [isTokenLoading, setIsTokenLoading] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);

  const getAccounts = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetAllMT5AccountApi(true);
      if (data?.success) {
        const liveAccounts = data?.data?.filter((account) => account?.isLive) || [];
        setAccounts(liveAccounts);
        setSelectedAccount(liveAccounts[0]?.login ?? null);
      } else {
        setAccounts([]);
      }
    } catch (error) {
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSocialTradingToken = async () => {
    if (!selectedAccount) return;
    setIsTokenLoading(true);
    try {
      const params = { loginId: selectedAccount };
      const { data } = await GetSocialTradingTokenApi(params);
      if (data?.success) setToken(data?.data);
      else setToken(null);
    } catch (error) {
      setToken(null);
    } finally {
      setIsTokenLoading(false);
    }
  };

  useEffect(() => {
    getAccounts();
  }, []);

  useEffect(() => {
    setToken(null);
    setRegistrationUrl(null);
    if (selectedAccount) getSocialTradingToken();
  }, [selectedAccount]);

  useEffect(() => {
    if (token || registrationUrl) setIframeLoading(true);
  }, [token, registrationUrl]);

  const handleRegisterNow = () => {
    setRegistrationUrl(SOCIAL_TRADING_REGISTRATION_URL);
  };

  const iframeContent = useMemo(() => `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <base href="${SOCIAL_TRADING_HOST}/widgets/" />
    </head>
    <body>
      <st-client-app
        id="501"
        st-product-host="${SOCIAL_TRADING_HOST}"
        st-assets-path="/portal/assets"
        st-widget-lang="en"
        st-widget-theme="light"
        st-widget-token="${token}">
      </st-client-app>
      <script src="${SOCIAL_TRADING_HOST}/widgets/main.js" defer></script>
    </body>
    </html>
  `, [token]);

  if (isLoading) return <Loader />;

  if (!accounts?.length) return <CreateLiveAccount />;

  return (
    <div className="h-full w-full space-y-4">
      <SegmentedComponent
        options={accounts.map((item) => item?.login)}
        value={selectedAccount}
        setValue={setSelectedAccount}
        block={isMobile}
      />

      <div className={`relative w-full overflow-hidden rounded-xl border border-[#E8E8E8] bg-white ${isMobile ? "h-[calc(100dvh-260px)] min-h-[320px]" : "h-[calc(100vh-150px)]"}`}>
        {(isTokenLoading || iframeLoading) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
            <Loader />
          </div>
        )}

        {token ? (
          <iframe
            srcDoc={iframeContent}
            title="Copy Trading"
            frameBorder="0"
            width="100%"
            height="100%"
            className="border-0"
            onLoad={() => setIframeLoading(false)}
          />
        ) : registrationUrl ? (
          <iframe
            key={registrationUrl}
            src={registrationUrl}
            title="Copy Trading Registration"
            frameBorder="0"
            width="100%"
            height="100%"
            className="border-0"
            onLoad={() => setIframeLoading(false)}
          />
        ) : (
          !isTokenLoading && (
            <div className="flex h-full w-full flex-col items-center justify-center gap-5">
              <p className="text-lg font-medium text-center px-4">
                You have not registered with our social trading platform.
              </p>
              <button
                type="button"
                className="bg-primary text-white font-medium px-4 py-2 rounded-lg"
                onClick={handleRegisterNow}
              >
                Register Now
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SocialTrading;
